import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import type { AvatarConfig } from "@shared/types";
import { useLocalProfile } from "../local/useLocalProfile";
import { getBattlePlayerId } from "../multiplayer/battleIdentity";
import { AvatarPreview } from "../components/AvatarEditor";
import { GameImage } from "../game/ui/GameImage";
import { sfx } from "../game/audio";
import { useDrawRoom } from "../drawit/useDrawRoom";
import { DrawCanvas, DRAW_COLORS, BRUSH_SIZES, type DrawCanvasApi } from "../drawit/DrawCanvas";
import { DrawGuessKeyboard } from "../drawit/DrawGuessKeyboard";
import { wordChoiceImage } from "../drawit/wordImages";
import type { DrawJoinOpts } from "../drawit/types";

interface Props {
  codeOverride?: string;
  identity?: { id: string; displayName: string; avatarConfig: AvatarConfig };
  embedded?: boolean;
}

/** DrawIt Controller — a student's iPad: join, draw on your turn, guess
 *  otherwise, see feedback + score. */
export function DrawController({ codeOverride, identity, embedded }: Props = {}) {
  const nav = useNavigate();
  const { profile } = useLocalProfile();
  const [params] = useSearchParams();
  const code = (codeOverride ?? params.get("code") ?? "").toUpperCase();

  const myIdentity = identity ?? (profile ? { id: getBattlePlayerId(profile.id, code, "join"), displayName: profile.displayName, avatarConfig: profile.avatarConfig } : null);
  const meId = myIdentity?.id;
  const joinOpts = useMemo<DrawJoinOpts | null>(
    () => (myIdentity && code ? { code, role: "player", me: myIdentity } : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [meId, code],
  );
  const { client, state, error } = useDrawRoom(joinOpts);
  const [color, setColor] = useState(DRAW_COLORS[0]!);
  const [brushWidth, setBrushWidth] = useState<number>(BRUSH_SIZES[1]);
  const canvasApiRef = useRef<DrawCanvasApi | null>(null);
  const [feedback, setFeedback] = useState<{ correct: boolean; text?: string; key: number } | null>(null);
  const [history, setHistory] = useState<string[]>([]);

  // Guess feedback (private to this device) + right/wrong SFX + correct-announce nudge.
  useEffect(() => {
    if (!client) return;
    return client.on((e) => {
      if (e.type === "guess-feedback") {
        setFeedback({ correct: e.correct, text: e.text, key: Date.now() });
        if (e.correct) sfx.correct();
        else { sfx.wrong(); setHistory((h) => [...h, e.text ?? ""].slice(-5)); }
      }
    });
  }, [client]);
  useEffect(() => { setHistory([]); setFeedback(null); }, [state?.roundIndex]);

  useEffect(() => { if (!identity && !profile) nav("/", { replace: true }); }, [identity, profile, nav]);
  if (!identity && !profile) return null;

  if (!code) {
    return <div className="quizzle-ctrl"><p className="quizzle-error">No room code.</p>{!embedded && <button className="btn btn-green" onClick={() => nav("/drawit")}>Enter a code</button>}</div>;
  }
  if (error) {
    return <div className="quizzle-ctrl"><p className="quizzle-error">⚠️ {error}</p>{!embedded && <button className="btn btn-ghost" onClick={() => nav("/drawit")}>← Back</button>}</div>;
  }

  const me = state?.players.find((p) => p.id === meId);
  const ranked = [...(state?.players ?? [])].sort((a, b) => b.score - a.score);
  const myRank = me ? ranked.findIndex((p) => p.id === me.id) + 1 : 0;
  const isMyTurn = !!state && state.drawerId === meId;
  const drawer = state?.players.find((p) => p.id === state?.drawerId);

  // CHOOSING
  if (state?.phase === "choosing") {
    if (isMyTurn) {
      return (
        <div className="quizzle-ctrl">
          <h1>Pick a word to draw!</h1>
          <div className="drawit-word-col">
            {(state.wordOptions ?? []).map((w) => (
              <button key={w} className="drawit-word-btn" onClick={() => client?.chooseWord(state.wordOptions!.indexOf(w))}>
                <GameImage className="drawit-word-img" src={wordChoiceImage(w)} alt="" fallbackLabel={w[0]?.toUpperCase() ?? "?"} />
                <span>{w}</span>
              </button>
            ))}
          </div>
        </div>
      );
    }
    return (
      <div className="quizzle-ctrl">
        <h1>🖍️ {drawer?.displayName ?? "Someone"} is picking a word…</h1>
        <p className="quizzle-dim">Get ready to guess!</p>
      </div>
    );
  }

  // DRAWING
  if (state?.phase === "drawing") {
    const blanks = state.wordLength ? Array.from({ length: state.wordLength }, () => "_").join(" ") : "";
    return (
      <div className="quizzle-ctrl quizzle-ctrl-q drawit-ctrl-draw">
        <p className="drawit-blanks-sm">{isMyTurn ? "Draw it!" : `${drawer?.displayName ?? "Someone"} is drawing`} {blanks && `· ${blanks}`}</p>
        <div className="drawit-canvas-wrap">
          <DrawCanvas client={client} interactive={isMyTurn} initialStrokes={state.strokes} resetKey={state.roundIndex} color={color} width={brushWidth} apiRef={canvasApiRef} className="drawit-canvas-small" />
        </div>
        {isMyTurn ? (
          <div className="drawit-toolbar">
            {DRAW_COLORS.map((c) => (
              <button key={c} className={`drawit-swatch ${color === c ? "is-on" : ""}`} style={{ background: c }} onClick={() => setColor(c)} aria-label={`Color ${c}`} />
            ))}
            <span className="drawit-toolbar-sep" aria-hidden />
            {BRUSH_SIZES.map((w) => (
              <button key={w} className={`drawit-sizebtn ${brushWidth === w ? "is-on" : ""}`} onClick={() => setBrushWidth(w)} aria-label={`Brush size ${w}`}>
                <span className="drawit-sizebtn-dot" style={{ width: 6 + w, height: 6 + w }} />
              </button>
            ))}
            <button className="btn btn-ghost" onClick={() => canvasApiRef.current?.undo()}>↩ Undo</button>
            <button className="btn btn-ghost" onClick={() => canvasApiRef.current?.clear()}>🗑 Clear</button>
          </div>
        ) : me?.guessedCorrect ? (
          <div className="drawit-got-it">✓ You got it! +{me.lastDelta}</div>
        ) : (
          <DrawGuessKeyboard
            key={`${state.roundIndex}-${history.length}`}
            wordLength={state.wordLength ?? 5}
            bank={state.letterBank ?? []}
            onSubmit={(text) => client?.guess(text)}
          />
        )}
        {feedback && !feedback.correct && (
          <p key={feedback.key} className="drawit-wrong-pop">✗ Not quite — try again!</p>
        )}
        {history.length > 0 && (
          <div className="drawit-guess-history">{history.map((h, i) => <span key={i}>{h}</span>)}</div>
        )}
      </div>
    );
  }

  // REVEAL
  if (state?.phase === "reveal") {
    const gotIt = (me?.lastDelta ?? 0) > 0;
    const wasDrawer = state.drawerId === meId;
    return (
      <div className={`quizzle-ctrl quizzle-ctrl-reveal ${wasDrawer || gotIt ? "is-right" : "is-wrong"}`}>
        <h1>{wasDrawer ? "🎨 Nice drawing!" : gotIt ? "✓ Correct!" : "The word was…"}</h1>
        {!wasDrawer && gotIt && <div className="quizzle-ctrl-plus">+{me?.lastDelta}</div>}
        <div className="drawit-reveal-row">
          <GameImage className="drawit-reveal-img" src={wordChoiceImage(state.revealedWord ?? "")} alt="" fallbackLabel="?" />
          <div className="drawit-reveal-word">{state.revealedWord}</div>
        </div>
        <p className="quizzle-dim">Score {me?.score ?? 0} · Rank #{myRank}</p>
      </div>
    );
  }

  // PODIUM
  if (state?.phase === "podium") {
    return (
      <div className="quizzle-ctrl quizzle-ctrl-podium">
        <div className="quizzle-ctrl-rank">#{myRank}</div>
        <h1>{myRank === 1 ? "🏆 You won!" : myRank <= 3 ? "🎉 Podium!" : "Good game!"}</h1>
        <p className="quizzle-dim">Final score {me?.score ?? 0} · {ranked.length} players</p>
        <p className="quizzle-dim">Stay here — the host can start another round!</p>
      </div>
    );
  }

  // LOBBY / waiting.
  return (
    <div className="quizzle-ctrl">
      <div className="quizzle-ctrl-joined">
        <span className="quizzle-ctrl-avatar"><AvatarPreview cfg={myIdentity?.avatarConfig ?? {}} /></span>
        <h1>You're in!</h1>
        <p className="quizzle-dim">{state?.players.length ?? 0} players</p>
        <p className="quizzle-dim">Look up at the big screen — DrawIt starts when the host is ready.</p>
      </div>
      {!embedded && <button className="btn btn-ghost" onClick={() => nav("/drawit")}>Leave</button>}
    </div>
  );
}
